(() => {
  if (window.cngPdpSale) return;
  const thresholds = [0, 20000, 30000, 45000];
  const discounts = [15, 20, 25, 30];
  let cartData = null;
  let running = false;
  let pending = false;
  let refreshTimer;
  const money = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  function tierFor(cents) {
    return thresholds.reduce((tier, threshold, index) => cents >= threshold ? index : tier, 0);
  }
  function updateCartDisplay() {
    document.querySelectorAll('[data-cng-pdp-tiers]').forEach((table) => {
      const active = table.dataset.active === 'true';
      // USD thresholds cannot be applied to an unconverted presentment currency.
      const usd = cartData?.currency === 'USD';
      table.hidden = !active || (cartData !== null && !usd);
      const section = table.closest('[id^="MainProduct-"]');
      const sticky = section?.querySelector('[data-cng-sticky-progress]');
      if (sticky) sticky.hidden = !active || !usd;
      if (!cartData || !usd) return;
      const tier = tierFor(cartData.cents);
      const text = tier === 3
        ? `Sale items: ${money(cartData.cents)} · Maximum 30% off tier reached`
        : `Sale items: ${money(cartData.cents)} · Add ${money(thresholds[tier + 1] - cartData.cents)} more for ${discounts[tier + 1]}% off`;
      table.querySelector('[data-cng-tier-nudge]').textContent = text;
      table.querySelector('progress').value = Math.min(cartData.cents / 100, 450);
      table.querySelectorAll('[data-tier]').forEach((row) => {
        row.classList.toggle('is-current', Number(row.dataset.tier) === tier);
        row.toggleAttribute('aria-current', Number(row.dataset.tier) === tier);
      });
      if (sticky) sticky.textContent = text;
    });
  }
  async function refreshCart() {
    if (!document.querySelector('[data-cng-pdp-tiers]')) return;
    if (running) { pending = true; return; }
    running = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}?sections=flash-sale-cart-data&_t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart data unavailable');
      const data = await response.json();
      const doc = new DOMParser().parseFromString(data['flash-sale-cart-data'] || '', 'text/html');
      const element = doc.querySelector('[data-cng-eligible-cart]');
      const cents = Number(element?.dataset.cents);
      if (!element || !Number.isFinite(cents) || cents < 0) throw new Error('Invalid cart data');
      cartData = { cents, currency: element.dataset.currency };
      updateCartDisplay();
    } catch {
      document.querySelectorAll('[data-cng-tier-nudge]').forEach((node) => {
        node.textContent = 'Cart progress is temporarily unavailable. Discount tiers are shown below.';
      });
      document.querySelectorAll('[data-cng-sticky-progress]').forEach((node) => { node.hidden = true; });
    } finally {
      running = false;
      if (pending) { pending = false; refreshCart(); }
    }
  }
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCart, 100);
  }
  function syncDeliveryApps(section) {
    const active = section.querySelector('[data-cng-sale-state]')?.dataset.active === 'true';
    section.querySelectorAll('[data-cng-delivery-app]').forEach((app) => {
      if (!app.querySelector('.simesy-text-timer')) return;
      app.querySelector('[data-cng-app-original]').hidden = active;
      app.querySelector('[data-cng-app-sale]').hidden = !active;
    });
  }
  function applyState(node, active) {
    node.dataset.active = String(active);
    if (node.matches('[data-cng-sale-content]')) {
      node.querySelector('[data-cng-original-content]').hidden = active;
      node.querySelector('[data-cng-active-content]').hidden = !active;
    } else updateCartDisplay();
    const section = node.closest('[id^="MainProduct-"]');
    if (section) syncDeliveryApps(section);
  }
  function init(section) {
    if (section.dataset.cngPdpReady) return;
    section.dataset.cngPdpReady = 'true';
    section.querySelectorAll('[data-cng-sale-state]').forEach((node) => {
      if (node.dataset.contentMode === 'delivery') {
        const target = node.querySelector('[data-cng-active-content]');
        const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) {
          walker.currentNode.textContent = walker.currentNode.textContent.replace(/\b\d+\s*(?:(?:[-–—]|to)\s*\d+\s*)?(?:working\s+|business\s+)?(?:days?|weeks?)\b/gi, '4–6 weeks');
        }
      }
      node.addEventListener('cng:sale-state-changed', (event) => applyState(node, event.detail.active));
      const start = Date.parse(node.dataset.saleStart);
      const end = Date.parse(node.dataset.saleEnd);
      const zoned = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d+)?(?:Z|[+-]\d\d:\d\d)$/;
      applyState(node, zoned.test(node.dataset.saleStart) && zoned.test(node.dataset.saleEnd) && start < end && start <= Date.now() && Date.now() < end);
    });
    new MutationObserver(() => syncDeliveryApps(section)).observe(section, { childList: true, subtree: true });
    syncDeliveryApps(section);
    const sticky = section.querySelector('[data-cng-pdp-sticky]');
    const form = section.querySelector(`#product-form-${section.dataset.section}`);
    const mainButton = form?.querySelector('[name="add"]');
    if (!sticky || !mainButton) return;
    sticky.hidden = false;
    document.body.classList.add('cng-has-pdp-sticky');
    const updateStickyHeight = () => document.body.style.setProperty('--cng-pdp-sticky-height', `${sticky.getBoundingClientRect().height}px`);
    new ResizeObserver(updateStickyHeight).observe(sticky);
    updateStickyHeight();
    const button = sticky.querySelector('[data-cng-sticky-add]');
    const picker = section.querySelector('variant-radios, variant-selects');
    function syncSticky() {
      button.disabled = mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true';
      button.textContent = mainButton.classList.contains('loading') ? 'Adding…' : mainButton.querySelector('span')?.textContent.trim() || mainButton.textContent.trim();
      sticky.querySelectorAll('[data-cng-sticky-option]').forEach((select) => {
        const index = Number(select.dataset.cngStickyOption);
        const fieldset = picker?.querySelectorAll('fieldset')[index];
        const value = fieldset ? fieldset.querySelector('input:checked')?.value : picker?.querySelectorAll('select')[index]?.value;
        if (value !== undefined) select.value = value;
      });
    }
    sticky.addEventListener('change', (event) => {
      const select = event.target.closest('[data-cng-sticky-option]');
      if (!select || !picker) return;
      const index = Number(select.dataset.cngStickyOption);
      const fieldset = picker.querySelectorAll('fieldset')[index];
      const input = fieldset ? [...fieldset.querySelectorAll('input')].find((node) => node.value === select.value) : picker.querySelectorAll('select')[index];
      if (!input) return;
      if (fieldset) input.checked = true; else input.value = select.value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      syncSticky();
    });
    picker?.addEventListener('change', syncSticky);
    button.addEventListener('click', () => { if (!button.disabled) mainButton.click(); });
    new MutationObserver(syncSticky).observe(mainButton, { attributes: true, childList: true, subtree: true, characterData: true });
    syncSticky();
  }
  const rawFetch = window.fetch;
  window.fetch = function(...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || String(args[0]);
    const result = rawFetch.apply(this, args);
    if (/\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/.test(url)) {
      result.then((response) => { if (response.ok) scheduleRefresh(); }, () => {});
    }
    return result;
  };
  function initAll() {
    document.querySelectorAll('[id^="MainProduct-"]').forEach(init);
    refreshCart();
  }
  document.addEventListener('cart:updated', scheduleRefresh);
  document.addEventListener('cart:refresh', scheduleRefresh);
  window.addEventListener('pageshow', scheduleRefresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleRefresh(); });
  document.addEventListener('shopify:section:load', initAll);
  window.cngPdpSale = { tierFor };
  initAll();
})();
